import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import "../style.css";
import { View, Flex, Text } from "@adobe/react-spectrum";
import { formatTime, formatTimestamp } from "../../../utils";
import { colorPalette } from "../../../constants/constants";
import { setHistoryfilterChatCount } from "../../../actions/chat";
import storeRTK from "../../../store/storeRTK";
import { MissedChatIcon } from "../../common/Icons";

interface IChatListProps {
  chat: any;
  activeChatId?: string;
  handleActiveChatId: (id: string) => void;
}

// Helper to get initials from name
const getInitials = (name: string) => {
  if (!name) return "N A";
  const words = name.trim().split(" ");
  const initials =
    words.length === 1
      ? words[0].charAt(0)
      : words[0].charAt(0) + words[1].charAt(0);
  return initials.toUpperCase();
};

const ChatList: React.FC<IChatListProps> = ({
  chat,
  activeChatId,
  handleActiveChatId,
}) => {
  const history = useSelector((state: any) => state.history);
  const common = useSelector((state: any) => state.common);

  useEffect(() => {
    storeRTK.dispatch(setHistoryfilterChatCount(history?.chat?.length));
  }, []);

  const chatWithComputedValues = useMemo(() => {
    return chat?.map((chatItem: any, index: number) => {
      const name = chatItem?.customer?.name || "N A";
      const initials = getInitials(name);
      const backgroundColor = colorPalette[index % colorPalette.length];

      return {
        ...chatItem,
        initials,
        backgroundColor,
        formattedConnectedTime: formatTimestamp(chatItem?.self?.connectedTime || chatItem?.customer?.connectedTime),
      };
    });
  }, [chat]);

  return (
    <View UNSAFE_className="chat-list-container">
      {chatWithComputedValues?.length > 0 &&
        chatWithComputedValues.map((chatItem: any) => {
          const { connectedTime, endTime } = chatItem?.self || {};
          const { formattedDifference } = formatTime(connectedTime, endTime);

          return (
            <div
              key={chatItem.id}
              onClick={() => handleActiveChatId(chatItem.id)}
            >
              <Flex
                alignItems={"center"}
                UNSAFE_className={`chat-list-section ${
                  chatItem.id === activeChatId ? "chat-list-active" : ""
                }`}
              >
                <Flex
                  width="40px"
                  height="40px"
                  justifyContent="center"
                  alignItems="center"
                  UNSAFE_className="agent-avatar"
                  UNSAFE_style={{
                    backgroundColor: chatItem.backgroundColor,
                    borderRadius: "50%",
                  }}
                >
                  <Text UNSAFE_className="call-monitor-agent-text">
                    {chatItem.initials}
                  </Text>
                </Flex>
                <Flex direction="column" width={"80%"} marginStart={7}>
                  <Flex justifyContent={"space-between"}>
                    <Text UNSAFE_className="chat-list-title">
                      {chatItem?.customer?.name}
                    </Text>
                    {chatItem.unreadMessages > 0 &&
                      common?.tab !== "History" && (
                        <Text UNSAFE_className="chat-unread-count">
                          {chatItem.unreadMessages}
                        </Text>
                      )}
                  </Flex>
                  <Flex>
                    <Flex
                      alignItems={"center"}
                      UNSAFE_className="chatlist-missed-icon"
                    >
                      {chatItem?.self?.missed && (
                        <Text marginX={5}>
                          <MissedChatIcon />
                        </Text>
                      )}
                    </Flex>
                    <Flex justifyContent={"space-between"} width="100%">
                      <Text UNSAFE_className="chat-list-description">
                        {chatItem.formattedConnectedTime || ""}
                      </Text>
                      {common?.tab === "History" && !chatItem?.self?.missed && (
                        <Text UNSAFE_className="chat-list-description">
                          {formattedDifference}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </div>
          );
        })}
    </View>
  );
};

export default ChatList;
